/**
 * Post-auth redirect handling.
 *
 * Everything a login/MFA screen sends the browser to is attacker-influenceable
 * (`?redirect=`, `?returnTo=`, router state from a crafted link), so it passes
 * through `sanitizeAppRedirect` first. The allowlist is deliberately narrow:
 * same-origin paths under `/app`. Nothing else is a legitimate post-login
 * destination, and anything wider is an open redirect.
 */

const APP_ROOT = '/app'

/** Control characters and backslashes are used to smuggle a host past a naive prefix check. */
const hasUnsafeChars = (path: string): boolean =>
  Array.from(path).some((ch) => ch === '\\' || ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)

/**
 * True if any path segment is a dot-segment. Checks the raw and percent-decoded
 * forms, so `%2e%2e` cannot slip past. Only the path is inspected — a literal
 * ".." inside a query value or hash is data, not traversal.
 */
function hasDotSegment(path: string): boolean {
  const pathOnly = path.split(/[?#]/)[0] ?? ''
  let decoded = pathOnly
  try {
    decoded = decodeURIComponent(pathOnly)
  } catch {
    // Malformed percent-encoding — fall back to the raw form.
  }
  return [pathOnly, decoded].some((candidate) =>
    candidate.split('/').some((segment) => segment === '.' || segment === '..')
  )
}

/** The requested location as a router-navigable path. */
export function locationToPath(loc: {
  pathname: string
  search?: string
  hash?: string
}): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

/**
 * Returns the value if it is a safe in-app destination, otherwise `null`.
 * Accepts `/app`, `/app/...`, `/app?...`, `/app#...` and nothing else — so
 * `//evil.com`, `https://evil.com/app`, `/application/x`, `/app\@evil.com`
 * and `/app/../login` are all rejected.
 */
export function sanitizeAppRedirect(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null

  const path = value.trim()
  if (!path.startsWith(APP_ROOT)) return null
  if (hasUnsafeChars(path)) return null

  const boundary = path.charAt(APP_ROOT.length)
  if (boundary !== '' && boundary !== '/' && boundary !== '?' && boundary !== '#') return null

  // `/app/../login` stays same-origin, so it is not an open redirect — but it
  // escapes /app, which breaks this function's contract. Reject traversal
  // outright rather than reasoning about how many segments it climbs.
  if (hasDotSegment(path)) return null

  return path
}

/**
 * Where to send the user after a successful login: the `?redirect=` param
 * first (survives a full page load, e.g. a notification-email deep link),
 * then the location stashed in router state, then Home.
 */
export function resolveLoginRedirect(opts: {
  redirectParam?: string | null
  fromState?: { pathname: string; search?: string; hash?: string } | null
}): string {
  const fromParam = sanitizeAppRedirect(opts.redirectParam)
  if (fromParam) return fromParam

  const fromState = opts.fromState ? sanitizeAppRedirect(locationToPath(opts.fromState)) : null
  if (fromState) return fromState

  return APP_ROOT
}

/**
 * The `/login` URL to bounce an unauthenticated request to, carrying the
 * requested path so login can return the user there.
 */
export function loginUrlFor(loc: { pathname: string; search?: string; hash?: string }): string {
  const target = sanitizeAppRedirect(locationToPath(loc))
  if (!target || target === APP_ROOT) return '/login'
  return `/login?redirect=${encodeURIComponent(target)}`
}
